import * as React from 'react';

import { useChartContext } from '@mui/x-charts/context/ChartProvider';
import { useSvgRef, useXScale } from '@mui/x-charts/hooks';

type MousePosition = {
    viewportPoint: {
        x: number;
        y: number;
    };
    svgPoint: {
        x: number;
        y: number;
    };
    pointerType: 'mouse' | 'touch' | 'pen';
    xAxisValue: Date;
};

type PointerData = {
    /** Unique identifier for this pointer */
    pointerId: number;
    /** X-coordinate relative to the viewport */
    clientX: number;
    /** Y-coordinate relative to the viewport */
    clientY: number;
    /** X-coordinate relative to the document, including scroll offset */
    pageX: number;
    /** Y-coordinate relative to the document, including scroll offset */
    pageY: number;
    /** The DOM element that was the target of this pointer event */
    target: EventTarget | null;
    /** Timestamp when the event occurred */
    timeStamp: number;
    /** Type of pointer event: 'pointerdown', 'pointermove', 'pointerup', etc. */
    type: string;
    /** Whether this is the primary pointer in a multi-pointer scenario */
    isPrimary: boolean;
    /** Pressure value, ranges from 0 to 1 (1 is maximum pressure) */
    pressure: number;
    /** Width of the contact area in CSS pixels */
    width: number;
    /** Height of the contact area in CSS pixels */
    height: number;
    /** Indicates the type of pointing device: 'mouse', 'touch', or 'pen' */
    pointerType: string;
    /** Reference to the original browser PointerEvent */
    srcEvent: PointerEvent;
};

type InteractionEvent = CustomEvent<
    {
        activeGestures: Record<string, boolean>;
        centroid: { x: number; y: number };
        customData: { [p: string]: unknown };
        gestureName: string;
        phase: 'end' | 'start' | 'cancel' | 'ongoing';
        pointers: PointerData[];
        srcEvent: Event;
        target: EventTarget;
        timeStamp: number;
    } & { srcEvent: PointerEvent }
>;

export function useMouseTracker(): MousePosition {
    const { instance } = useChartContext();
    const xScale = useXScale<'time'>();
    const svgRef = useSvgRef();

    const [mousePosition, setMousePosition] =
        React.useState<MousePosition | null>(null);

    React.useEffect(() => {
        const moveEndHandler = instance.addInteractionListener(
            'moveEnd',
            event => {
                if (!event.detail.activeGestures.pan) {
                    setMousePosition(null);
                }
            }
        );

        const gestureHandler = (event: InteractionEvent) => {
            const svg = svgRef.current;
            const viewportPoint = svg.createSVGPoint();

            // Set the point's coordinates to the event's client position
            viewportPoint.x = event.detail.centroid.x;
            viewportPoint.y = event.detail.centroid.y;

            // Do some funky math to transform the point to the SVG coordinate system
            const svgPoint = viewportPoint.matrixTransform(
                svg.getScreenCTM().inverse()
            );

            setMousePosition({
                xAxisValue: xScale.invert(svgPoint.x),
                viewportPoint: {
                    x: event.detail.centroid.x,
                    y: event.detail.centroid.y,
                },
                svgPoint: svgPoint,
                pointerType: event.detail.srcEvent
                    .pointerType as MousePosition['pointerType'],
            });
        };

        const moveHandler = instance.addInteractionListener(
            'move',
            gestureHandler
        );
        const panHandler = instance.addInteractionListener(
            'pan',
            gestureHandler
        );

        return () => {
            moveHandler.cleanup();
            panHandler.cleanup();
            moveEndHandler.cleanup();
        };
    }, [instance, svgRef.current.width, svgRef.current.height, xScale]);

    return mousePosition;
}
